import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  FiBarChart2,
  FiClock,
  FiCode,
  FiHeadphones,
  FiMapPin,
  FiShield,
  FiUsers,
  FiZap,
} from "react-icons/fi";
import "./About.css";
import ClientCarousel from "../../components/ClientCarousel";

const pillars = [
  {
    icon: <FiShield />,
    title: "20+ Years of Experience",
    desc: "Over two decades of expertise...",
  },
  {
    icon: <FiCode />,
    title: "End-to-End Solutions",
    desc: "From APIs and communication platforms...",
  },
  {
    icon: <FiHeadphones />,
    title: "Dedicated Support & Customization",
    desc: "Real technical support...",
  },
  {
    icon: <FiZap />,
    title: "Scalable Infrastructure",
    desc: "Secure, DLT & TRAI compliant...",
  },
];

const About = () => {
  const [stats, setStats] = useState({
    clients: 0,
    years: 0,
    uptime: 0,
    products: 0,
  });

  const statsRef = useRef(null);
  const didAnimate = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || didAnimate.current) return;

        didAnimate.current = true;

        const duration = 1600;
        const start = performance.now();

        const tick = (now) => {
          const progress = Math.min((now - start) / duration, 1);

          setStats({
            clients: Math.round(1000 * progress),
            years: Math.round(20 * progress),
            uptime: 99.9 * progress,
            products: Math.round(9 * progress),
          });

          if (progress < 1) {
            requestAnimationFrame(tick);
          }
        };

        requestAnimationFrame(tick);
      },
      { threshold: 0.35 }
    );

    if (statsRef.current) observer.observe(statsRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <div className="about-page">
      <section className="about-hero">
        <div className="hero-scanline" aria-hidden="true" />
        <div className="hero-gridoverlay" aria-hidden="true" />
        <div className="container">
          <p className="section-label">About Shankarshan Technologies</p>
          <h1 className="about-hero-title">
            Empowering Businesses Through
            <span className="highlight"> Communication & Technology</span>
          </h1>
          <p className="about-hero-text">
            Shankarshan Technologies is a leading Bulk SMS service provider in
            India, providing dependable, fast, and steady communication
            solutions for groups of all sizes. We focus on Bulk SMS, IVR
            services, OTP SMS, transactional messaging, and DLT-compliant
            communication systems that assist agencies in connecting to their
            customers instantly.
          </p>
          <div className="about-hero-trust">
            <span>
              <FiUsers /> 1000+ active clients
            </span>
            <span>
              <FiClock /> 20+ years in service
            </span>
            <span>
              <FiMapPin /> Headquartered in Durgapur
            </span>
          </div>
        </div>
      </section>
      <section className="about-overview">
        <div className="container about-grid">
          <div className="about-copy">
            <p className="section-label">Who We Are</p>
            <h2 className="section-heading">
              More than a technology provider a long-term technology partner
            </h2>
            <p>
              We are Shankarshan Technologies a trusted partner for businesses
              across industries. From startups to large enterprises,
              organizations rely on us for reliable communication systems and
              tailored software solutions that align with their unique business
              needs.
            </p>
            <p>
              With over 20+ years of industry experience, we combine deep
              technical expertise with practical business understanding to
              deliver solutions that drive real results. Unlike standard service
              providers, we don't just offer APIs or tools we deliver complete,
              customized solutions tailored to your business workflows.
            </p>
          </div>
          <div className="about-pillars">
            {pillars.map((item) => (
              <article className="pillar-card" key={item.title}>
                <div className="pillar-icon">{item.icon}</div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
      <section className="about-timeline">
        <div className="container">
          <p className="section-label">What We Do</p>
          <h2 className="section-heading">
            A comprehensive range of services to power modern businesses
          </h2>
          <div className="timeline-grid">
            <article className="timeline-card">
              <span className="timeline-year">Communication</span>
              <h3>Bulk SMS & Messaging</h3>
              <p>
                Transactional and promotional Bulk SMS, WhatsApp Business API
                and Chatbots, and Email and Notification Systems — keeping your
                customers informed at every step.
              </p>
            </article>

            <article className="timeline-card">
              <span className="timeline-year">Voice & Calls</span>
              <h3>Cloud IVR & Call Center</h3>
              <p>
                Cloud IVR systems paired with inbound and outbound Call Center
                solutions — automating communication workflows and improving
                team efficiency at scale.
              </p>
            </article>

            <article className="timeline-card">
              <span className="timeline-year">Software</span>
              <h3>Custom Enterprise Applications</h3>
              <p>
                Stock Broker Backoffice Applications, Real Estate CRM and
                Management Systems, Business Automation Tools, and fully
                customized enterprise applications built for your exact needs.
              </p>
            </article>

            <article className="timeline-card">
              <span className="timeline-year">Our Approach</span>
              <h3>Built Around Your Business</h3>
              <p>
                We understand your business needs first, then design tailored
                solutions, implement with reliability and security, and provide
                ongoing support and scaling as you grow.
              </p>
            </article>
          </div>
        </div>
      </section>
      <section className="about-cta">
        <div className="container cta-inner">
          <h2>Ready to build together?</h2>
          <p>
            We believe in building long-term partnerships, not just delivering
            services. Whether you're looking to improve communication, automate
            processes, or build custom software we're here to help. Get in touch
            today to start your journey.
          </p>
          <div className="cta-actions">
            <Link to="/contact" className="hero-btn-primary">
              Get Free Demo
            </Link>
            <Link to="/services/sms" className="hero-btn-secondary">
              Explore Services
            </Link>
          </div>
        </div>
      </section>









































      <section className="about-stats" ref={statsRef}>
        <div className="container">
          <p className="section-label">By The Numbers</p>
          <h2 className="section-heading stats-heading">
            Operational metrics that matter
          </h2>
          <div className="stats-grid">
            <article className="stat-card">
              <FiUsers className="stat-icon" />
              <div className="stat-number">{stats.clients}+</div>
              <div className="stat-label-text">Businesses Trusted</div>
            </article>
            <article className="stat-card">
              <FiClock className="stat-icon" />
              <div className="stat-number">{stats.years}+</div>
              <div className="stat-label-text">Years of Expertise</div>
            </article>
            <article className="stat-card">
              <FiZap className="stat-icon" />
              <div className="stat-number">{stats.uptime.toFixed(1)}%</div>
              <div className="stat-label-text">Uptime SLA</div>
            </article>
            <article className="stat-card">
              <FiBarChart2 className="stat-icon" />
              <div className="stat-number">{stats.products}</div>
              <div className="stat-label-text">Core Products</div>
            </article>
          </div>
        </div>
      </section>
      <ClientCarousel />
    </div>
  );
};

export default About;
